'use client';

import Stack from '@mui/material/Stack';
import dynamic from 'next/dynamic';

const condition = {
  time: 1659985678986,
  blocks: [
    {
      type: 'header',
      data: {
        text: 'Terms and Conditions for Trybe',
        level: 2,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'Last updated: June 30, 2024',
      },
    },
    {
      type: 'paragraph',
      data: {
        text: "Welcome to Trybe! These terms and conditions outline the rules and regulations for the use of Trybe's platform, located at <a href='https://trybe.fm' style='color:#ffd369;'>Trybe.fm</a>.",
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'By accessing this website and using our services, we assume you accept these terms and conditions. Do not continue to use Trybe if you do not agree to all of the terms and conditions stated on this page.',
      },
    },
    {
      type: 'header',
      data: {
        text: '1. Definitions',
        level: 3,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: '"Platform" refers to Trybe’s website and mobile applications.',
      },
    },
    {
      type: 'paragraph',
      data: {
        text: '"We", "Us", "Our" refers to Trybe, based in Quebec, Canada.',
      },
    },
    {
      type: 'paragraph',
      data: {
        text: '"You", "User", "Organizer" refers to the person accessing or using the platform.',
      },
    },
    {
      type: 'header',
      data: {
        text: '2. Use of the Platform',
        level: 3,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'You must be at least 18 years old to use this platform.',
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'You agree to use the platform only for lawful purposes and in a way that does not infringe the rights of, restrict, or inhibit anyone else’s use and enjoyment of the platform.',
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'We reserve the right to suspend or terminate your access to the platform at any time without notice if you breach these terms and conditions.',
      },
    },
    {
      type: 'header',
      data: {
        text: '3. User Accounts',
        level: 3,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'To access certain features of the platform, you must register for an account and provide accurate, complete, and up-to-date information.',
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'You are responsible for maintaining the confidentiality of your account information and password. You agree to accept responsibility for all activities that occur under your account.',
      },
    },
    {
      type: 'header',
      data: {
        text: '4. Event Listings',
        level: 3,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'Organizers can create and manage events on the platform. By creating an event, you represent and warrant that all information provided is accurate and that you have the legal right to host the event.',
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'We reserve the right to remove any event listing that violates these terms and conditions or that we deem inappropriate or misleading.',
      },
    },
    {
      type: 'header',
      data: {
        text: '5. Payments and Fees',
        level: 3,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: "Users may purchase tickets for events through the platform. All transactions are processed securely using Stripe as our payment provider. By using our services, you agree to Stripe's <a style='color:#ffd369;' href='https://stripe.com/legal'>Terms of Service</a> and <a style='color:#ffd369;' href='https://stripe.com/privacy'>Privacy Policy</a>.",
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'Organizers may be subject to fees for listing events or using certain features. These fees will be clearly disclosed at the time of the transaction.',
      },
    },
    {
      type: 'header',
      data: {
        text: '6. Refunds and Cancellations',
        level: 3,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'Refund policies for event tickets are determined by the event organizer and will be clearly stated on the event listing.',
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'We are not responsible for any disputes regarding refunds or cancellations. All such issues should be addressed directly with the event organizer.',
      },
    },
    {
      type: 'header',
      data: {
        text: '7. Content and Intellectual Property',
        level: 3,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'Users may upload content, such as event descriptions, photos, and videos. By uploading content, you grant us a non-exclusive, worldwide, royalty-free license to use, reproduce, modify, and display such content.',
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'You must ensure that you have the necessary rights to grant us this license for any content you upload.',
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'All intellectual property rights in the platform and its content are owned by us or our licensors.',
      },
    },
    {
      type: 'header',
      data: {
        text: '8. Privacy',
        level: 3,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: "Our <a href='https://trybe.fm/privacy' style='color:#ffd369;'>Privacy Policy</a> explains how we collect, use, and share your personal information. By using the platform, you agree to the terms of the Privacy Policy.",
      },
    },
    {
      type: 'header',
      data: {
        text: '9. Limitation of Liability',
        level: 3,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'The platform is provided on an "as is" basis, and we make no warranties or representations about the platform or its content.',
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'We will not be liable for any direct, indirect, incidental, special, or consequential damages arising from your use of the platform.',
      },
    },
    {
      type: 'header',
      data: {
        text: '10. Indemnification',
        level: 3,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'You agree to indemnify and hold us harmless from any claims, damages, losses, liabilities, and expenses arising from your use of the platform or your breach of these terms and conditions.',
      },
    },
    {
      type: 'header',
      data: {
        text: '11. Changes to Terms',
        level: 3,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'We may update these terms and conditions from time to time. We will notify you of any changes by posting the new terms on the platform. Your continued use of the platform after any changes indicates your acceptance of the new terms.',
      },
    },
    {
      type: 'header',
      data: {
        text: '12. Governing Law',
        level: 3,
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'These terms and conditions are governed by and construed in accordance with the laws of Quebec, Canada, and you submit to the exclusive jurisdiction of the courts in that location.',
      },
    },
    {
      type: 'paragraph',
      data: {
        text: 'If you have any questions about these terms and conditions, please contact us at <a href="mailto:info@trybe.fm" style="color:#ffd369;">info@trybe.fm</a>',
      },
    },
  ],
  version: '2.19.0',
};

const EditorBlock = dynamic(() => import('src/components/editor/EditorJs'), { ssr: false });

export default function TermsConditionView() {
  return (
    <Stack sx={{ alignItems: 'center' }}>
      <EditorBlock data={condition} holder="editorjs" />
    </Stack>
  );
}
